import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  ArrowRight, Zap, CheckCircle2, TrendingUp, Clock, Users,
  Award, Globe, Code, Target, Rocket, Settings, Search, Database
} from 'lucide-react';

const AIImplementationServices = () => {
  const services = [
    {
      icon: Target,
      title: 'AI Strategy & Implementation',
      description: 'Transform your AI vision into production-ready systems in weeks',
      features: [
        'Rapid AI readiness assessment',
        'Custom AI roadmap & architecture',
        'Production deployment',
        'ROI-focused outcomes'
      ]
    },
    {
      icon: Rocket,
      title: 'ML Model Development & Deployment',
      description: 'Build and deploy machine learning models at enterprise scale',
      features: [
        'Predictive analytics',
        'NLP & document processing',
        'Computer vision',
        'Real-time ML pipelines'
      ]
    },
    {
      icon: Zap,
      title: 'Generative AI Integration',
      description: 'Leverage GPT, Claude, and LLaMA for business automation',
      features: [
        'Custom LLM fine-tuning',
        'RAG systems',
        'AI chatbots & assistants',
        'Document generation'
      ]
    },
    {
      icon: Settings,
      title: 'AI System Optimization',
      description: 'Enhance existing AI systems for better performance and lower cost',
      features: [
        'Model performance tuning',
        'Cost reduction architecture',
        'Scalability improvements',
        '24/7 monitoring'
      ]
    },
    {
      icon: Database,
      title: 'Data Pipeline Engineering',
      description: 'Build the data infrastructure your AI systems need to succeed',
      features: [
        'ETL & ELT pipelines',
        'Vector database setup',
        'Real-time data streams',
        'Data quality automation'
      ]
    },
    {
      icon: Search,
      title: 'AI Readiness Assessment',
      description: 'Understand exactly where AI can move the needle in your business',
      features: [
        'Use case discovery workshop',
        'Data readiness audit',
        'Build vs. buy analysis',
        '90-day implementation roadmap'
      ]
    }
  ];

  const caseStudies = [
    {
      industry: 'Insurance',
      challenge: '500+ daily insurance statements processed manually',
      solution: 'AI-powered OCR and NLP system with automated CRM updates',
      result: '95% reduction in processing time. Zero errors in data extraction.',
      timeline: '3 weeks',
      link: '/case-studies/insurance-automation'
    },
    {
      industry: 'Healthcare',
      challenge: 'Manual risk assessment causing delayed patient interventions',
      solution: 'ML-powered predictive model with real-time patient monitoring',
      result: '40% improvement in early risk detection. 25% fewer readmissions.',
      timeline: '6 weeks',
      link: '/case-studies/healthcare-ai'
    },
    {
      industry: 'Manufacturing',
      challenge: 'Inaccurate inventory planning causing stockouts and overstock',
      solution: 'AI-driven demand forecasting with multi-factor analysis',
      result: '50% reduction in forecast error. $2M saved in inventory costs annually.',
      timeline: '5 weeks',
      link: '/case-studies/manufacturing-ai'
    }
  ];

  const implementationProcess = [
    {
      step: '01',
      title: 'AI Readiness Assessment',
      description: 'We audit your data, systems, and workflows to identify the highest-ROI AI opportunities',
      duration: '3-5 days'
    },
    {
      step: '02',
      title: 'Architecture & Roadmap',
      description: 'Custom AI architecture design with a phased implementation plan and fixed timeline',
      duration: '1 week'
    },
    {
      step: '03',
      title: 'Build & Iterate',
      description: 'Agile sprints with weekly working software — not presentations. You see progress every week',
      duration: '2-6 weeks'
    },
    {
      step: '04',
      title: 'Deploy & Optimize',
      description: 'Production deployment with monitoring, optimization, and ongoing support included',
      duration: '1-2 weeks'
    }
  ];

  const whyChooseUs = [
    {
      icon: Award,
      title: 'Senior Engineers Only',
      description: 'No junior developers. Direct access to architects with 10+ years of AI/ML implementation experience.'
    },
    {
      icon: Rocket,
      title: 'Weekly Working Software',
      description: 'See progress every week with working prototypes, not slide decks. Agile sprints with continuous delivery.'
    },
    {
      icon: Clock,
      title: 'Fixed Timeline Commitments',
      description: 'Aggressive deadlines with accountability. 95% on-time delivery rate with transparent project tracking.'
    },
    {
      icon: CheckCircle2,
      title: 'Production-Ready Code',
      description: 'Enterprise-grade, scalable, secure code ready for production from day one. No throwaway prototypes.'
    },
    {
      icon: Code,
      title: 'Full-Stack Expertise',
      description: 'End-to-end implementation from data pipelines to frontend interfaces. No dependency on third parties.'
    },
    {
      icon: Globe,
      title: 'Global Delivery',
      description: 'Teams across USA, India, and Israel delivering 24/7. We work in your timezone with real-time collaboration.'
    }
  ];

  const techStack = [
    {
      category: 'LLMs',
      technologies: ['OpenAI GPT-4o', 'Claude 3.5 Sonnet', 'Gemini Pro', 'Llama 3', 'Mistral']
    },
    {
      category: 'Frameworks',
      technologies: ['LangChain', 'LangGraph', 'LlamaIndex', 'Haystack']
    },
    {
      category: 'ML Platforms',
      technologies: ['TensorFlow', 'PyTorch', 'scikit-learn', 'Hugging Face', 'Keras']
    },
    {
      category: 'Vector Databases',
      technologies: ['Pinecone', 'Weaviate', 'Chroma', 'pgvector', 'Qdrant']
    },
    {
      category: 'Cloud AI',
      technologies: ['Azure OpenAI', 'AWS Bedrock', 'Google Vertex AI', 'AWS SageMaker']
    },
    {
      category: 'MLOps',
      technologies: ['Kubernetes', 'Docker', 'MLflow', 'Kubeflow', 'Weights & Biases']
    }
  ];

  return (
    <>
      <Helmet>
        <title>AI Implementation Services | Rapid AI Deployment in 3-8 Weeks | Agility</title>
        <meta name="description" content="Leading AI implementation company delivering production-ready AI systems in 3-8 weeks. 200+ successful implementations. 99% client satisfaction. Senior AI engineers, not consultants. Get rapid AI deployment with measurable ROI." />
        <meta name="keywords" content="AI implementation services, AI implementation company, rapid AI deployment, AI development services, AI consulting and implementation, machine learning implementation, AI system implementation, enterprise AI implementation, fast AI implementation, AI project implementation" />
        <link rel="canonical" href="https://www.agilitytech.ai/ai-implementation-services" />
        <meta property="og:title" content="AI Implementation Services | Rapid AI Deployment in 3-8 Weeks" />
        <meta property="og:description" content="200+ successful AI implementations. 3-8 week timelines. 99% satisfaction. Senior engineers deploy production-ready AI systems." />
        <meta property="og:url" content="https://www.agilitytech.ai/ai-implementation-services" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://www.agilitytech.ai/agility hex fonts.png" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "name": "AI Implementation Services",
            "description": "Rapid AI implementation and deployment services delivering production-ready systems in 3-8 weeks",
            "provider": {
              "@type": "Organization",
              "name": "Agility",
              "url": "https://www.agilitytech.ai"
            },
            "areaServed": "Worldwide",
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": "4.95",
              "reviewCount": "200",
              "bestRating": "5"
            }
          })}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden bg-gradient-to-br from-blue-900 via-black to-teal-900">
        <div className="absolute inset-0 opacity-20">
          <div
            className="absolute inset-0 bg-[linear-gradient(30deg,#14b8a6_12%,transparent_12.5%,transparent_87%,#14b8a6_87.5%,#14b8a6),linear-gradient(150deg,#14b8a6_12%,transparent_12.5%,transparent_87%,#14b8a6_87.5%,#14b8a6)]"
            style={{ backgroundSize: '80px 140px' }}
          />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-teal-500/10 border border-teal-500/30 rounded-full mb-8">
            <Zap className="w-4 h-4 text-teal-400" />
            <span className="text-teal-300 text-sm font-medium">Rapid AI Implementation</span>
          </div>

          <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight">
            AI Implementation in <span className="text-teal-400">Weeks, Not Months</span>
          </h1>

          <p className="text-xl text-gray-300 leading-relaxed mb-8 max-w-3xl mx-auto">
            Production-ready AI systems deployed in 3-8 weeks. Senior AI engineers deliver measurable results,
            not consulting decks. 200+ successful implementations with 99% client satisfaction.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
            <Link
              to="/contact"
              className="group inline-flex items-center justify-center gap-2 bg-teal-500 hover:bg-teal-600 text-white px-8 py-4 rounded-lg font-semibold transition-all"
            >
              Get Your AI Roadmap
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link
              to="/case-studies"
              className="group inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 text-white px-8 py-4 rounded-lg font-semibold backdrop-blur-sm border border-white/20 transition-all"
            >
              View Case Studies
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-4xl mx-auto">
            <div className="text-center">
              <div className="text-4xl font-bold text-teal-400 mb-2">3-8</div>
              <div className="text-gray-400">Weeks to Production</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-teal-400 mb-2">200+</div>
              <div className="text-gray-400">Implementations Delivered</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-teal-400 mb-2">99%</div>
              <div className="text-gray-400">Client Satisfaction</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-teal-400 mb-2">95%</div>
              <div className="text-gray-400">On-Time Delivery</div>
            </div>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Comprehensive <span className="text-teal-600">AI Implementation Services</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              From strategy to production deployment — every service is scoped for speed and measurable outcomes
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <div key={index} className="fade-in-up bg-gradient-to-br from-gray-50 to-white rounded-xl p-8 border-2 border-gray-200 hover:border-teal-500 transition-all">
                  <Icon className="w-12 h-12 text-teal-600 mb-6" />
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">{service.title}</h3>
                  <p className="text-gray-600 mb-6">{service.description}</p>
                  <div className="flex flex-wrap gap-2">
                    {service.features.map((feature, idx) => (
                      <span key={idx} className="px-3 py-1 bg-teal-50 text-teal-700 rounded-full text-sm font-medium">
                        {feature}
                      </span>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Case Studies */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Real Implementations, <span className="text-teal-600">Real Results</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Concrete case studies we've shipped — not whitepaper concepts
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            {caseStudies.map((study, index) => (
              <Link
                key={index}
                to={study.link}
                className="fade-in-up bg-gradient-to-br from-teal-50 to-white rounded-xl p-8 border border-teal-200 hover:border-teal-400 hover:shadow-lg transition-all group"
              >
                <div className="text-sm font-semibold text-teal-600 mb-4">{study.industry}</div>
                <h3 className="text-xl font-bold text-gray-900 mb-4 group-hover:text-teal-700 transition-colors">Challenge</h3>
                <p className="text-gray-700 mb-4">{study.challenge}</p>

                <h3 className="text-xl font-bold text-gray-900 mb-4">Solution</h3>
                <p className="text-gray-700 mb-4">{study.solution}</p>

                <h3 className="text-xl font-bold text-gray-900 mb-4">Results</h3>
                <p className="text-gray-700 mb-6">{study.result}</p>

                <div className="flex items-center justify-between pt-6 border-t border-teal-200">
                  <div>
                    <div className="text-sm text-gray-500">Timeline</div>
                    <div className="font-semibold text-gray-900">{study.timeline} to production</div>
                  </div>
                  <div className="flex items-center gap-1 text-teal-600 font-semibold text-sm group-hover:translate-x-1 transition-transform">
                    Read more <ArrowRight className="w-4 h-4" />
                  </div>
                </div>
              </Link>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/case-studies"
              className="inline-flex items-center gap-2 text-teal-600 hover:text-teal-700 font-semibold"
            >
              View All Case Studies
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Implementation Process */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Our <span className="text-teal-600">Implementation Process</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              From discovery to production in 3-8 weeks — with working software at every stage
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {implementationProcess.map((step, index) => (
              <div key={index} className="fade-in-up relative">
                <div className="text-center">
                  <div className="w-20 h-20 bg-teal-500 text-white rounded-full flex items-center justify-center text-3xl font-bold mx-auto mb-6">
                    {step.step}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{step.title}</h3>
                  <p className="text-gray-600 mb-4">{step.description}</p>
                  <div className="inline-flex items-center gap-2 px-3 py-1 bg-teal-50 text-teal-700 rounded-full text-sm font-medium">
                    <Clock className="w-4 h-4" />
                    {step.duration}
                  </div>
                </div>
                {index < implementationProcess.length - 1 && (
                  <div className="hidden lg:block absolute top-10 left-full w-full h-0.5 bg-teal-200 -ml-4" />
                )}
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 bg-teal-500 hover:bg-teal-600 text-white px-8 py-4 rounded-lg font-semibold transition-all"
            >
              Start Your Assessment
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-24 bg-gradient-to-br from-gray-900 to-black text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Why Leading Companies Choose <span className="text-teal-400">Agility?</span>
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              The fastest, most reliable AI implementation partner in the industry.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {whyChooseUs.map((item, index) => {
              const Icon = item.icon;
              return (
                <div key={index} className="fade-in-up bg-white/5 backdrop-blur-sm rounded-xl p-8 border border-white/10 hover:border-teal-500/50 transition-all">
                  <Icon className="w-12 h-12 text-teal-400 mb-6" />
                  <h3 className="text-xl font-bold mb-3">{item.title}</h3>
                  <p className="text-gray-300">{item.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Tech Stack */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Our <span className="text-teal-600">Technology Stack</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We work with every major AI platform and framework — model-agnostic, infrastructure-flexible
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {techStack.map((category, index) => (
              <div key={index} className="fade-in-up bg-white rounded-xl p-8 border border-gray-200">
                <h3 className="text-xl font-bold text-gray-900 mb-4">{category.category}</h3>
                <div className="flex flex-wrap gap-2">
                  {category.technologies.map((tech, idx) => (
                    <span key={idx} className="px-3 py-1 bg-teal-50 text-teal-700 rounded-lg text-sm font-medium">
                      {tech}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 bg-gradient-to-br from-teal-500 to-teal-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to Implement AI in Weeks?
          </h2>
          <p className="text-xl text-teal-50 mb-8 max-w-2xl mx-auto">
            Get a free AI readiness assessment and custom implementation roadmap.
            See exactly how we'll deliver your AI system in 3-8 weeks.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 bg-white text-teal-600 hover:bg-gray-100 px-8 py-4 rounded-lg font-semibold transition-all text-lg"
          >
            Schedule AI Assessment
            <ArrowRight className="w-5 h-5" />
          </Link>
          <p className="mt-6 text-teal-50">
            or call us at <span className="font-semibold">+91-7041402034</span>
          </p>
        </div>
      </section>
    </>
  );
};

export default AIImplementationServices;
